/**
 * The `meeting` scene: the original AgoraMeeting-parity room (gallery, moderation, chat,
 * whiteboard, screen share, settings, recording indicator), now with i18n + testids + a manual
 * token-renewal control. Behaviour is unchanged from the pre-scenario demo.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  RecordingIndicator,
  VirtualBackgroundPreview,
  DeviceService,
  type DeviceGrouped,
  type DeviceSelection,
  type VirtualBackgroundOption,
} from '@nimeet/ui-kit-react';
import { buildInviteLink } from '../config';
import { describeError } from '../meeting/session';
import type { MeetingRole } from '../meeting/session';
import {
  appendStroke,
  clearBoard,
  decodeBoardMessage,
  encodeStroke,
  undoLastStroke,
  type Board,
} from '../meeting/board';
import { useI18n } from '../i18n';
import {
  Banner,
  BoardPanel,
  ChatPanel,
  ControlBar,
  Modal,
  ParticipantsPanel,
  RemoteAudio,
  VideoTile,
  useSessionState,
} from '../ui';
import { ConfirmModal, InviteModal, RoomHeader, useNoticeToast } from './common';
import type { RoomContext } from './types';

export function MeetingRoom({ context }: { context: RoomContext }) {
  const { session, client, notify, onLeave } = context;
  const { t } = useI18n();
  const state = useSessionState(
    useCallback((listener) => session.subscribe(listener), [session]),
    useCallback(() => session.getState(), [session]),
  );
  useNoticeToast(session, notify);
  const [panel, setPanel] = useState<'none' | 'chat' | 'participants' | 'board'>('participants');
  const [dialog, setDialog] = useState<null | 'invite' | 'settings' | 'manage' | 'end'>(null);
  const [board, setBoard] = useState<Board>([]);
  const [boardColor, setBoardColor] = useState('#3b82f6');
  const [boardWidth, setBoardWidth] = useState(3);
  const [boardEraser, setBoardEraser] = useState(false);
  const [background, setBackground] = useState<'none' | 'blur' | 'image'>('none');
  const [backgroundImage, setBackgroundImage] = useState<string | null>(null);
  const [recordingStartedAt, setRecordingStartedAt] = useState<number | null>(null);
  const [devices, setDevices] = useState<{
    group: DeviceGrouped;
    selection: DeviceSelection;
  } | null>(null);
  const deviceService = useMemo(() => new DeviceService(), []);
  const [clock, setClock] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setClock(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    let alive = true;
    void (async () => {
      const listed = await deviceService.listDevices();
      if (!alive || !listed.ok) return;
      setDevices({ group: listed.value, selection: deviceService.getSelection() });
    })();
    const unsubscribe = deviceService.subscribe(() => {
      setDevices((current) =>
        current === null ? current : { ...current, selection: deviceService.getSelection() },
      );
    });
    return () => {
      alive = false;
      unsubscribe();
      deviceService.dispose();
    };
  }, [deviceService]);

  const recordingSeconds =
    recordingStartedAt === null
      ? null
      : Math.max(0, Math.floor((clock - recordingStartedAt) / 1000));
  const [quality, setQuality] = useState('720p');
  const [, force] = useState(0);

  const localStream = client.getLocalStream('camera');
  const screenStream = client.getLocalStream('screen');
  const local = state.participants.find((entry) => entry.isLocal);
  const isHost = local?.role === 'host';

  useEffect(() => {
    const timer = window.setInterval(() => {
      void session.refreshStats().catch(() => undefined);
      force((value) => value + 1);
    }, 4000);
    return () => window.clearInterval(timer);
  }, [session]);

  useEffect(() => {
    const offData = client.on('data-received', ((event: {
      from: number;
      payload: Uint8Array;
      topic?: string;
    }) => {
      if (event.topic !== 'whiteboard') return;
      const decoded = decodeBoardMessage(event.payload, event.from);
      if (!decoded) return;
      if ('stroke' in decoded) setBoard((current) => appendStroke(current, decoded.stroke));
      else if ('snapshot' in decoded) setBoard(decoded.snapshot);
      else setBoard(clearBoard());
    }) as never);
    return offData;
  }, [client]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement)
        return;
      if (event.key === 'm') void session.toggleMic();
      if (event.key === 'v') void session.toggleCamera();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [session]);

  const shareStroke = useCallback(
    (stroke: Parameters<typeof encodeStroke>[0]) => {
      setBoard((current) => appendStroke(current, stroke));
      void client.sendData({ payload: encodeStroke(stroke), reliable: true, topic: 'whiteboard' });
    },
    [client],
  );

  const leave = useCallback(async () => {
    await session.leave().catch(() => undefined);
    onLeave();
  }, [session, onLeave]);

  const invite = buildInviteLink(state.channel ?? '', local?.displayName, undefined, 'meeting');
  const sideVisible = panel !== 'none';
  const backgroundOptions: VirtualBackgroundOption[] = [
    { id: 'none', label: t('meeting.settings.vbg.none'), mode: 'none' },
    { id: 'blur', label: t('meeting.settings.vbg.blur'), mode: 'blur' },
    {
      id: 'image',
      label: t('meeting.settings.vbg.image'),
      mode: 'image',
      previewUrl: backgroundImage ?? undefined,
    },
  ];
  const errorText = state.errorKey ? t(state.errorKey) : state.error;

  return (
    <main
      className={`nm-room${sideVisible ? '' : ' no-side'}`}
      data-testid="room"
      data-scene="meeting"
    >
      <section className="nm-stage">
        <RoomHeader
          scene="meeting"
          channel={state.channel ?? ''}
          onLeave={() => setDialog('end')}
          meta={
            <>
              <RecordingIndicator
                active={recordingStartedAt !== null}
                elapsedSeconds={recordingSeconds}
                compact
              />
              <button
                className={`nm-btn${recordingStartedAt === null ? '' : ' is-danger'}`}
                aria-pressed={recordingStartedAt !== null}
                onClick={() =>
                  setRecordingStartedAt(recordingStartedAt === null ? Date.now() : null)
                }
                data-testid="room-recording"
              >
                {recordingStartedAt === null ? t('room.recording.start') : t('room.recording.stop')}
              </button>
              <button
                className="nm-btn"
                onClick={() => {
                  void session.renew().then(() => notify(t('room.renewed')));
                }}
                data-testid="room-renew"
              >
                {t('room.renew')}
              </button>
            </>
          }
        />
        {state.phase === 'reconnecting' ? (
          <Banner tone="warn">{t('room.reconnecting')}</Banner>
        ) : null}
        {errorText ? (
          <Banner tone="error">
            <span data-testid="room-error">{errorText}</span>
          </Banner>
        ) : null}
        {state.locked ? <Banner tone="warn">{t('room.locked')}</Banner> : null}
        <div className="nm-grid" data-testid="room-grid">
          {screenStream ? (
            <div className="nm-tile">
              <video
                ref={(element) => {
                  if (element) element.srcObject = screenStream;
                }}
                autoPlay
                playsInline
                muted
              />
              <div className="nm-tile-name">{t('room.share.self')}</div>
            </div>
          ) : null}
          {state.participants.map((participant) => (
            <VideoTile
              key={participant.uid}
              participant={participant}
              stream={
                participant.isLocal ? localStream : client.getRemoteStream(participant.uid, 'video')
              }
              background={
                participant.isLocal
                  ? background === 'none'
                    ? null
                    : (backgroundImage ?? background)
                  : null
              }
            />
          ))}
        </div>
        {state.participants
          .filter((participant) => !participant.isLocal)
          .map((participant) => (
            <RemoteAudio
              key={`audio-${participant.uid}`}
              stream={client.getRemoteStream(participant.uid, 'audio')}
            />
          ))}
        <ControlBar
          state={state}
          onToggleMic={() => void session.toggleMic()}
          onToggleCamera={() => void session.toggleCamera()}
          onToggleShare={() => {
            void session.toggleScreenShare().catch((error) => notify(describeError(error)));
          }}
          onToggleChat={() => {
            setPanel(panel === 'chat' ? 'none' : 'chat');
            session.openChat(panel !== 'chat');
          }}
          onToggleParticipants={() => setPanel(panel === 'participants' ? 'none' : 'participants')}
          onToggleBoard={() => {
            setPanel(panel === 'board' ? 'none' : 'board');
            session.openBoard(panel !== 'board');
          }}
          onOpenSettings={() => setDialog('settings')}
          onOpenInvite={() => setDialog('invite')}
          onOpenManage={() => setDialog('manage')}
          onRaiseHand={() => void session.setHandRaised(!(local?.handRaised ?? false))}
          onRenew={() => {
            void session.renew().then(() => notify(t('room.renewed')));
          }}
          onLeave={() => setDialog('end')}
        />
      </section>

      {sideVisible ? (
        <aside className="nm-side">
          {panel === 'participants' ? (
            <ParticipantsPanel
              state={state}
              localUid={state.localUid}
              canModerate={isHost === true}
              onMuteAll={() => {
                const muted = session.muteAll();
                notify(t('toast.mutedCount', { count: muted }));
              }}
              onKick={(uid) => {
                session.kick(uid);
                notify(t('toast.participantRemoved'));
              }}
              onRole={(uid: number, role: MeetingRole) => {
                session.setRole(uid, role);
                void session.hostAction(uid, 'role', role);
              }}
            />
          ) : null}
          {panel === 'chat' ? (
            <ChatPanel
              state={state}
              onSend={(text) => void session.sendChat(text)}
              onClearHistory={() => {
                session.clearChatHistory();
                notify(t('toast.historyCleared'));
              }}
              onClose={() => setPanel('none')}
            />
          ) : null}
          {panel === 'board' ? (
            <div className="nm-card">
              <BoardPanel
                board={board}
                readOnly={isHost !== true}
                color={boardColor}
                width={boardWidth}
                eraser={boardEraser}
                onColor={setBoardColor}
                onWidth={setBoardWidth}
                onEraser={setBoardEraser}
                onStroke={shareStroke}
                onUndo={() => {
                  const next = undoLastStroke(board, state.localUid ?? undefined);
                  setBoard(next);
                  void client.sendData({
                    payload: new TextEncoder().encode(
                      JSON.stringify({ type: 'board:snapshot', board: next }),
                    ),
                    topic: 'whiteboard',
                  });
                }}
                onClear={() => {
                  setBoard(clearBoard());
                  void client.sendData({
                    payload: new TextEncoder().encode(JSON.stringify({ type: 'board:clear' })),
                    topic: 'whiteboard',
                  });
                }}
                onClose={() => setPanel('none')}
              />
            </div>
          ) : null}
        </aside>
      ) : null}

      {dialog === 'invite' ? (
        <InviteModal
          title={t('meeting.invite.title')}
          link={invite}
          note={t('meeting.invite.note')}
          count={state.participants.length}
          channel={state.channel}
          onCopy={() => {
            void navigator.clipboard?.writeText(invite).catch(() => undefined);
            notify(t('toast.inviteCopied'));
          }}
          onClose={() => setDialog(null)}
        />
      ) : null}

      {dialog === 'settings' ? (
        <Modal
          title={t('meeting.settings.title')}
          onClose={() => setDialog(null)}
          testId="settings-dialog"
        >
          <div className="nm-field">
            <span>{t('meeting.settings.devices')}</span>
            <div className="nm-row" style={{ flexWrap: 'wrap' }}>
              <select
                className="nm-select"
                aria-label={t('meeting.settings.defaultMic')}
                value={devices?.selection.microphoneId ?? ''}
                data-testid="settings-microphone"
                onChange={(event) =>
                  void deviceService.select('microphone', event.target.value || null)
                }
              >
                <option value="">{t('meeting.settings.defaultMic')}</option>
                {(devices?.group.microphones ?? []).map((device) => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {device.label}
                  </option>
                ))}
              </select>
              <select
                className="nm-select"
                aria-label={t('join.field.camera')}
                value={devices?.selection.cameraId ?? ''}
                data-testid="settings-camera"
                onChange={(event) =>
                  void deviceService.select('camera', event.target.value || null)
                }
              >
                <option value="">{t('meeting.settings.defaultCamera')}</option>
                {(devices?.group.cameras ?? []).map((device) => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {device.label}
                  </option>
                ))}
              </select>
              <button
                className="nm-btn"
                data-testid="settings-refresh"
                onClick={() => {
                  void (async () => {
                    const granted = await deviceService.requestPermission('both');
                    if (!granted.ok) return notify(t('toast.permissionDenied'));
                    const listed = await deviceService.listDevices();
                    if (!listed.ok) return notify(listed.error.message);
                    setDevices({ group: listed.value, selection: deviceService.getSelection() });
                  })();
                }}
              >
                {t('meeting.settings.allow')}
              </button>
            </div>
            <span className="nm-sub">
              {devices === null
                ? t('meeting.settings.enumerating')
                : t('meeting.settings.deviceCounts', {
                    microphones: devices.group.microphones.length,
                    cameras: devices.group.cameras.length,
                    speakers: devices.group.speakers.length,
                  })}
            </span>
          </div>
          <div className="nm-field">
            <span>{t('meeting.settings.quality')}</span>
            <select
              className="nm-select"
              aria-label={t('meeting.settings.quality')}
              value={quality}
              data-testid="settings-quality"
              onChange={(event) => {
                const value = event.target.value;
                setQuality(value);
                const profile =
                  value === '1080p'
                    ? { width: 1920, height: 1080, frameRate: 30, bitrateMax: 4000 }
                    : value === '360p'
                      ? { width: 640, height: 360, frameRate: 24, bitrateMax: 600 }
                      : { width: 1280, height: 720, frameRate: 30, bitrateMax: 2000 };
                void client
                  .setVideoEncoderConfiguration(profile)
                  .catch(() => notify(t('meeting.settings.qualityNext')));
              }}
            >
              <option value="1080p">
                {t('meeting.settings.qualityOption', { resolution: '1080p', fps: 30 })}
              </option>
              <option value="720p">
                {t('meeting.settings.qualityOption', { resolution: '720p', fps: 30 })}
              </option>
              <option value="360p">
                {t('meeting.settings.qualityOption', { resolution: '360p', fps: 24 })}
              </option>
            </select>
          </div>
          <div className="nm-field">
            <span>{t('meeting.settings.vbg')}</span>
            <VirtualBackgroundPreview
              options={backgroundOptions}
              selectedId={background}
              stream={localStream}
              displayName={local?.displayName ?? ''}
              videoOff={!state.cameraEnabled || !localStream}
              onSelect={(option) => {
                setBackground(option.mode);
                if (option.mode === 'image' && !backgroundImage)
                  notify(t('meeting.settings.vbg.uploadHint'));
              }}
            />
            <label className="nm-btn" style={{ cursor: 'pointer', marginTop: 8 }}>
              {t('meeting.settings.vbg.upload')}
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (!file) return;
                  const reader = new FileReader();
                  reader.onload = () => {
                    setBackgroundImage(String(reader.result));
                    setBackground('image');
                  };
                  reader.readAsDataURL(file);
                }}
              />
            </label>
            <span className="nm-sub">{t('meeting.settings.vbg.note')}</span>
          </div>
          <div className="nm-field">
            <span>{t('meeting.settings.session')}</span>
            <div className="nm-kv">
              <dt>{t('meeting.settings.mode')}</dt>
              <dd>{context.offline ? t('app.offlineTag') : t('meeting.settings.modeLive')}</dd>
              <dt>{t('meeting.settings.localUid')}</dt>
              <dd>{state.localUid ?? t('common.dash')}</dd>
              <dt>{t('meeting.settings.rtt')}</dt>
              <dd>{state.stats ? `${Math.round(state.stats.rttMs)} ms` : t('common.dash')}</dd>
              <dt>{t('meeting.settings.bitrate')}</dt>
              <dd>{state.stats ? `${state.stats.bitrateKbps} kbps` : t('common.dash')}</dd>
            </div>
          </div>
        </Modal>
      ) : null}

      {dialog === 'manage' ? (
        <Modal
          title={t('meeting.manage.title')}
          onClose={() => setDialog(null)}
          testId="manage-dialog"
        >
          <div className="nm-row" style={{ marginBottom: 12 }}>
            <button
              className="nm-btn"
              onClick={() => {
                const muted = session.muteAll();
                notify(t('toast.mutedCount', { count: muted }));
              }}
            >
              {t('meeting.manage.muteAll')}
            </button>
            <button
              className="nm-btn"
              onClick={() =>
                notify(session.toggleLock() ? t('toast.meetingLocked') : t('toast.meetingUnlocked'))
              }
            >
              {state.locked ? t('meeting.manage.unlock') : t('meeting.manage.lock')}
            </button>
          </div>
          <div className="nm-scroll" style={{ maxHeight: 220 }}>
            {state.participants
              .filter((entry) => !entry.isLocal)
              .map((participant) => (
                <div className="nm-person" key={participant.uid}>
                  <AvatarInline name={participant.displayName} />
                  <div className="nm-person-main">
                    <div className="nm-person-name">{participant.displayName}</div>
                    <div className="nm-person-meta">
                      {participant.role === 'host'
                        ? t('meeting.badge.host')
                        : t('join.role.audience')}
                    </div>
                  </div>
                  <button className="nm-btn" onClick={() => session.kick(participant.uid)}>
                    {t('meeting.manage.remove')}
                  </button>
                  <button
                    className="nm-btn"
                    onClick={() => {
                      const next: MeetingRole = participant.role === 'host' ? 'audience' : 'host';
                      session.setRole(participant.uid, next);
                      void session.hostAction(participant.uid, 'role', next);
                    }}
                  >
                    {participant.role === 'host'
                      ? t('meeting.participants.makeAudience')
                      : t('meeting.participants.makeHost')}
                  </button>
                </div>
              ))}
          </div>
          <p className="nm-sub">
            {t('meeting.manage.hostOnly', {
              allowed: isHost ? t('meeting.manage.true') : t('meeting.manage.false'),
            })}
          </p>
        </Modal>
      ) : null}

      {dialog === 'end' ? (
        <ConfirmModal
          title={t('room.leaveTitle')}
          body={
            isHost
              ? t('room.leaveBodyHost', { channel: state.channel ?? '' })
              : t('room.leaveBody', { channel: state.channel ?? '' })
          }
          confirmLabel={t('room.leave')}
          onConfirm={() => {
            void leave();
          }}
          onClose={() => setDialog(null)}
          testId="end-dialog"
        />
      ) : null}
    </main>
  );
}

function AvatarInline({ name }: { name: string }) {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
  return (
    <div className="nm-avatar-sm" aria-hidden="true">
      {initials || '?'}
    </div>
  );
}
